// src/composables/useSupportTicket.ts
import { computed, ref, unref, type MaybeRef } from 'vue';
import { api } from 'boot/axios';
import { trace } from 'boot/trace';
import { getResponseData, notifyError, notifyInfo } from 'src/utils';
import type {
  SupportTicket,
  SupportTicketFull,
  SupportTicketMessage,
  SupportTicketSummary,
} from 'src/types/supportTicket';

/**
 * Context-agnostic logic for a single Vivantio-backed support ticket. Owns the
 * generic support-ticket API calls (show / refresh / notes), the conversation
 * thread, and the synchronous-write concern: `inFlight` drives spinners while a
 * call is making its live round-trip to Vivantio, and a `502`
 * (`error.type === 'vivantio_api_error'`) surfaces a "try again later" message
 * rather than reporting success — nothing is persisted upstream on failure, so
 * the caller simply re-issues to retry.
 *
 * Keyed only by a ticket id, so it is reusable anywhere a ticket is shown. Raising
 * a ticket is a context-specific concern handled by the caller (e.g. the AR member
 * query endpoint); seed the resulting summary via `setTicket` or `initialSummary`.
 */
export function useSupportTicket(options: {
  ticketId: MaybeRef<number | null>;
  initialSummary?: SupportTicketSummary | null;
}) {
  const ticket = ref<SupportTicket | null>(options.initialSummary ?? null);
  const loading = ref(false);
  // Intent-specific write flags so callers can reflect exactly what is happening
  // (e.g. spinner on the right control) rather than a single generic busy state.
  const refreshing = ref(false);
  const sendingNote = ref(false);
  /** Any synchronous Vivantio write is in progress — handy for disabling controls. */
  const inFlight = computed(() => refreshing.value || sendingNote.value);

  /** Messages newest-last, mirroring the API's `vivantio_action_at` ordering. */
  const messages = computed<SupportTicketMessage[]>(() =>
    !!ticket.value && 'messages' in ticket.value ? ticket.value.messages : []
  );

  const isResolved = computed(() => !!ticket.value?.status?.resolved);
  const isClosed = computed(() => !!ticket.value?.status?.is_closed);
  /** The conversation accepts replies only while the ticket is open. */
  const canReply = computed(() => !isResolved.value && !isClosed.value);

  function resolveTicketId(): number | null {
    return unref(options.ticketId) ?? ticket.value?.id ?? null;
  }

  /** Seed (or replace) the ticket — e.g. from the member query response. */
  function setTicket(next: SupportTicket | null) {
    ticket.value = next;
  }

  /**
   * Run a synchronous Vivantio write, toggling the given intent flag and owning
   * the 502 handling. On a `502` a "try again later" message is shown and the
   * error re-thrown so the caller can stay put; other errors propagate untouched.
   */
  async function runWrite<T>(
    busy: typeof refreshing,
    work: () => Promise<T>
  ): Promise<T> {
    busy.value = true;
    try {
      return await work();
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status;
      if (status === 502) {
        notifyError(
          'We could not reach the support system just now. Please try again in a short while.'
        );
      }
      throw error;
    } finally {
      busy.value = false;
    }
  }

  /** Fetch the full ticket (show endpoint) — used when no summary was seeded. */
  async function load(): Promise<void> {
    const ticketId = resolveTicketId();
    if (!ticketId) return;
    loading.value = true;
    try {
      const response = await api.get(`/api/v1/support-tickets/${ticketId}`);
      ticket.value = getResponseData(response) as SupportTicket;
    } catch (error) {
      trace('Error loading support ticket', 'error', error);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Re-sync from Vivantio and merge any newer messages into the thread on screen.
   * Called on open (safety net for missed webhooks) and after adding a note (to
   * catch anything an agent submitted in the meantime).
   *
   * Returns whether the refresh brought any new content (a new action or a
   * changed status). When `notify` is set, surfaces an info message reflecting
   * that outcome — used by the manual "check for updates" action.
   *
   * @returns true when new content arrived, false otherwise.
   */
  async function refresh(options?: { notify?: boolean }): Promise<boolean> {
    if (refreshing.value) {
      return false;
    }

    const ticketId = resolveTicketId();
    if (!ticketId) return false;
    let hasUpdates = false;
    await runWrite(refreshing, async () => {
      const response = await api.post(
        `/api/v1/support-tickets/${ticketId}/refresh`
      );
      const refreshedTicket = getResponseData(response) as SupportTicketFull;

      if (!ticket.value) {
        ticket.value = refreshedTicket;
        hasUpdates = (refreshedTicket.messages?.length ?? 0) > 0;
        return;
      }

      const knownIds = new Set(
        (ticket.value.messages ?? []).map((message) => message.id)
      );
      const hasNewMessage = (refreshedTicket.messages ?? []).some(
        (message) => !knownIds.has(message.id)
      );
      const statusChanged =
        ticket.value.status?.label !== refreshedTicket.status?.label;
      hasUpdates = hasNewMessage || statusChanged;

      ticket.value = {
        ...ticket.value,
        status: refreshedTicket.status,
        group_name: refreshedTicket.group_name,
        assigned_to_summary: refreshedTicket.assigned_to_summary,
        messages: refreshedTicket.messages,
        last_synced_at: refreshedTicket.last_synced_at,
      } as SupportTicket;
    });

    if (options?.notify) {
      notifyInfo(
        hasUpdates
          ? 'Your ticket has been updated.'
          : 'Your ticket has no updates.'
      );
    }

    return hasUpdates;
  }

  /** Post a reply, then refresh so a concurrent agent message/action is not missed. */
  async function addNote(note: string): Promise<void> {
    const ticketId = resolveTicketId();
    if (!ticketId) return;
    const trimmed = note.trim();
    if (!trimmed) return;
    await runWrite(sendingNote, async () => {
      await api.post(`/api/v1/support-tickets/${ticketId}/notes`, {
        note: trimmed,
      });
    });
    await refresh();
  }

  return {
    ticket,
    messages,
    loading,
    refreshing,
    sendingNote,
    inFlight,
    isResolved,
    isClosed,
    canReply,
    setTicket,
    load,
    refresh,
    addNote,
  };
}
